import React, {useState, useEffect, useRef, useContext} from 'react';
import styles from './TeleOp.module.css';
import VideoFeed from './VideoFeed'
import KeyboardControls from './KeyboardControls'
import TeleOpRightPanel from "./TeleOpRightPanel";
import {SwapMainAppContext} from "../../context/SwapMainAppContext";
import {PRISMAX_BACKEND_Control_URL} from "../../configs/config";
import {decryptEncryptedUrl} from "../../utils/utils";
import {Link, useParams} from "react-router-dom";
import TeleopSessionCompleteModal from './TeleopSessionCompleteModal';
import FirstTimeCompleteModal from "../../context/FirstTimeCompleteModal";
import navRightArrow from "../../static_files/navRightArrow.svg";
import {isSessionExpiredError} from '../../utils/authErrors';

import useBackgroundColorChange from '../../utils/useBackgroundColorChange';

const TeleOp = () => {    
    const [isChatOpen, setIsChatOpen] = useState(true);
    const [isConnected, setIsConnected] = useState(false);
    const [videoSrc, setVideoSrc] = useState(null);
    const [videoSrc2, setVideoSrc2] = useState(null); // Second video source
    const [fps, setFps] = useState(0);
    const [isTeleOpMode, setIsTeleOpMode] = useState(false);
    const [isControlConnected, setIsControlConnected] = useState(false);
    const [controlData, setControlData] = useState(null);
    const [isQueuePanelOpen, setIsQueuePanelOpen] = useState(true);
    const [isControlPanelOpen, setIsControlPanelOpen] = useState(true);
    const [pressedKeysUI, setPressedKeysUI] = useState(new Set()); // For UI updates
    const [remainingTime, setRemainingTime] = useState(0); // Remaining control time in seconds
    const [sessionExpired, setSessionExpired] = useState(false); // Track if session was expired
    const [modalControlToken, setModalControlToken] = useState(null); // control token for completion modal
    const [isFirstTime, setIsFirstTime] = useState(false); // Track if it's user's first time
    const [showFirstTimeModal, setShowFirstTimeModal] = useState(false); // Show first time completion modal
    const [showSessionCompleteModal, setShowSessionCompleteModal] = useState(false); // Show session completion modal

    const {
        gatewayToken,
        showNotification,
        sessionUrlKey,
        setSessionUrlKey,
        robotDataDict,
        robotSlugToRobotId,
        handleUserSessionExpired
    } = useContext(SwapMainAppContext);

    // Get the robot data from the URL slug
    const {robotSlug} = useParams(); 
    const effectiveRobotId = robotSlugToRobotId[robotSlug] || '';
    const robotName = robotDataDict[effectiveRobotId]?.robotName || 'Robotic Arm Control';

    const wsRef = useRef(null);
    const ws2Ref = useRef(null); // Second WebSocket for second camera
    const controlWsRef = useRef(null);
    const frameCountRef = useRef(0);
    const lastFpsUpdateRef = useRef(Date.now());
    const keysPressed = useRef(new Set());
    const sliderKeysPressed = useRef(new Set());
    const pollIntervalRef = useRef(null);
    const controlTokenRef = useRef(null);
    const isConnectingRef = useRef(false);
    const hasConnectedRef = useRef(false);
    const countdownIntervalRef = useRef(null); // For countdown timer
    const isIntentionalDisconnectRef = useRef(false); // Track if disconnect was intentional
    const inactivityTimerRef = useRef(null); // Track inactivity timer
    const lastActivityTimeRef = useRef(Date.now()); // Track last user activity
    const expirationTimeoutRef = useRef(null); // Control token expiration timeout
    const userId = localStorage.getItem("userId");

    const stream1UrlRef = useRef(null);
    const stream2UrlRef = useRef(null);
    // Keyboard mapping and speeds configuration
    const KEYMAP_PLUS = {
        x: 'KeyW',
        y: 'KeyA',
        z: 'KeyQ',
        t: 'ArrowUp',
        r: 'ArrowLeft',
        g: 'KeyX'
    };

    const KEYMAP_MINUS = {
        x: 'KeyS',
        y: 'KeyD',
        z: 'KeyE',
        t: 'ArrowDown',
        r: 'ArrowRight',
        g: 'KeyZ'
    };

    // Normalized control for MotionGenerator: states in [-1, 0, 1]
    const AXIS_SPEEDS = {
        x: 1,
        y: 1,
        z: 1,
        t: 1,
        r: 1,
        g: 1
    };

    const POLL_RATE = 60;
    const INACTIVITY_TIMEOUT = 30000; // 30 seconds in milliseconds

    useBackgroundColorChange();  // change the background color for the new design

    // Track user activity
    const trackUserActivity = () => {
        lastActivityTimeRef.current = Date.now();
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = null;
        }
    };

    // Start inactivity timer
    const startInactivityTimer = () => {
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
        }

        inactivityTimerRef.current = setTimeout(() => {
            showNotification('warning', 'Session ended due to inactivity', '20000');
            handleEndTeleOp('inactivity');
        }, INACTIVITY_TIMEOUT);
    };

    // Stop inactivity timer
    const stopInactivityTimer = () => {
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = null;
        }
    };

    const handleControlDataReceived = (data) => {
        if (data.expires && data.expires * 1000 < Date.now()) {
            showNotification('error', 'Control token has expired. Please try refreshing the page or rejoining the queue.');
            return;
        }
        setControlData(data);
        hasConnectedRef.current = false;
    };

    // Connect to control WebSocket when in TeleOp mode
    useEffect(() => {
        if (isTeleOpMode && controlData && !hasConnectedRef.current && !isConnectingRef.current) {
            connectControlWebSocket();
        } else if (!isTeleOpMode) {
            disconnectControlWebSocket();
            setControlData(null);
            hasConnectedRef.current = false;
            isConnectingRef.current = false;
        }

        return () => {
            // Don't disconnect on cleanup if still in teleop mode
            if (!isTeleOpMode) {
                disconnectControlWebSocket();
            }
        };
    }, [isTeleOpMode, controlData]);

    async function fetchURLSessionKey(userId, gatewayToken) {
        const res = await fetch(`${PRISMAX_BACKEND_Control_URL}/session_url_decrypt_key`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${gatewayToken}`,
            },
            body: JSON.stringify({userId}),
        });

        if (!res.ok) throw new Error("Failed to get session key");

        const {key_b64, exp} = await res.json();

        const standardBase64 = key_b64
            .replace(/-/g, '+')
            .replace(/_/g, '/');

        const padding = (4 - (standardBase64.length % 4)) % 4;
        const paddedBase64 = standardBase64 + '='.repeat(padding);

        try {
            const rawKey = Uint8Array.from(atob(paddedBase64), c => c.charCodeAt(0));
            setSessionUrlKey(rawKey);

            setTimeout(() => fetchURLSessionKey(userId, gatewayToken), (exp * 1000 - Date.now()) - 60_000);

            // Return the key directly
            return rawKey;
        } catch (e) {
            console.error("Base64 decode error:", e);
            throw e;
        }
    }

    const connectControlWebSocket = async () => {
        if (isConnectingRef.current || hasConnectedRef.current) {
            console.log('Already connecting or connected, skipping...');
            return;
        }

        isConnectingRef.current = true;

        try {
            const userId = localStorage.getItem('userId');

            if (!userId || !gatewayToken) {
                showNotification('error', 'Authentication required. Please log in.');
                setIsTeleOpMode(false);
                isConnectingRef.current = false;
                return;
            }

            if (!controlData || !controlData.control_token) {
                showNotification('error', 'No control data available. Please join queue first.');
                setIsTeleOpMode(false);
                isConnectingRef.current = false;
                return;
            }

            // Check if token is expired
            if (controlData.expires && controlData.expires * 1000 < Date.now()) {
                showNotification('error', 'Control token has expired. Please rejoin the queue.');
                setIsTeleOpMode(false);
                setControlData(null);
                isConnectingRef.current = false;
                return;
            }

            const robotId = controlData.robotId || effectiveRobotId || 'arm1';

            // Ensure we have a session key
            let tempSessionUrlKey = sessionUrlKey;
            if (!tempSessionUrlKey) {
                try {
                    // Get the key directly from the function return value
                    tempSessionUrlKey = await fetchURLSessionKey(userId, gatewayToken);
                } catch (error) {
                    showNotification('error', 'Failed to get session key.');
                    setIsTeleOpMode(false);
                    isConnectingRef.current = false;
                    return;
                }
            }

            // Call the /use_robot API
            const response = await fetch(`${PRISMAX_BACKEND_Control_URL}/use_robot`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${gatewayToken}`
                },
                body: JSON.stringify({
                    robotId: robotId,
                    userId: userId
                })
            });

            if (!response.ok) {
                const error = await response.json().catch(() => ({}));
                console.error('use_robot error:', error);
                const message = error.error || error.msg || 'Failed to connect to robot.';
                setIsTeleOpMode(false);
                setControlData(null);
                isConnectingRef.current = false;

                if (isSessionExpiredError(message)) {
                    handleUserSessionExpired?.();
                    return;
                }

                showNotification('error', message);

                // If robot is busy, user needs to rejoin queue
                if (response.status === 409) {
                    showNotification('error', 'Robot is busy. Please rejoin the queue.');
                }
                return;
            }

            const data = await response.json();
            const {
                encrypted_url,
                encrypted_stream1_url,
                encrypted_stream2_url,
                control_token,
                expires,
                isFirstTime
            } = data;

            // Store if it's user's first time
            if (isFirstTime !== undefined) {
                setIsFirstTime(isFirstTime);
            }

            // Mark as connected before WebSocket connection
            hasConnectedRef.current = true;

            // Store the control token for WebSocket authentication
            controlTokenRef.current = control_token;

            // Decrypt the URL
            const wsUrl = await decryptEncryptedUrl(encrypted_url, tempSessionUrlKey);
            if (encrypted_stream1_url && encrypted_stream2_url) {
                stream1UrlRef.current = await
                    decryptEncryptedUrl(encrypted_stream1_url, tempSessionUrlKey);
                stream2UrlRef.current = await
                    decryptEncryptedUrl(encrypted_stream2_url, tempSessionUrlKey);
            }
            // Create WebSocket connection
            controlWsRef.current = new WebSocket(wsUrl);

            controlWsRef.current.onopen = () => {
                controlWsRef.current.send(JSON.stringify({
                    token: control_token
                }));
            };

            controlWsRef.current.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);

                    if (data.status === 'connected') {
                        setIsControlConnected(true);
                        startKeyboardPolling();
                        // Start countdown timer
                        startCountdown(expires);
                        // Start inactivity timer
                        startInactivityTimer();
                        isConnectingRef.current = false;
                    } else if (data.status === 'busy') {
                        showNotification('error', 'Robot is busy. Please rejoin the queue.');
                        setIsTeleOpMode(false);
                        setControlData(null);
                        hasConnectedRef.current = false;
                        isConnectingRef.current = false;
                        disconnectControlWebSocket();
                    } else if (data.status === 'unauthorized') {
                        showNotification('error', 'Invalid control token.');
                        setIsTeleOpMode(false);
                        setControlData(null);
                        hasConnectedRef.current = false;
                        isConnectingRef.current = false;
                        disconnectControlWebSocket();
                    } else if (data.status === 'expired' || data.status === 'session_expired') {
                        showNotification('warning', 'Your control session has expired.');
                        setSessionExpired(true);
                        handleEndTeleOp('timeout');
                    }
                } catch (error) {
                    console.error('Error parsing control message:', error);
                }
            };

            controlWsRef.current.onerror = (error) => {
                console.error('Control WebSocket error:', error);
                setIsControlConnected(false);
                hasConnectedRef.current = false;
                isConnectingRef.current = false;
                showNotification('error', 'Control connection error. Please try again.');
            };

            controlWsRef.current.onclose = () => {
                // console.log('Disconnected from control WebSocket');
                setIsControlConnected(false);
                setControlData(null);
                hasConnectedRef.current = false;
                isConnectingRef.current = false;
                stopKeyboardPolling();
            };

            // Set up token expiration handler (clear any previous one first)
            const expiresIn = expires * 1000 - Date.now();
            if (expirationTimeoutRef.current) {
                clearTimeout(expirationTimeoutRef.current);
                expirationTimeoutRef.current = null;
            }
            expirationTimeoutRef.current = setTimeout(() => {
                showNotification('warning', 'Control session expired. Please rejoin the queue.');
                setSessionExpired(true);
                handleEndTeleOp('timeout');
            }, Math.max(0, expiresIn));

        } catch (error) {
            console.error('Failed to connect to control WebSocket:', error);
            setIsControlConnected(false);
            setControlData(null);
            hasConnectedRef.current = false;
            isConnectingRef.current = false;
            showNotification('error', 'Failed to establish control connection.');
            setIsTeleOpMode(false);
        }
    };

    const disconnectControlWebSocket = async () => {
        if (controlWsRef.current) {
            if (controlWsRef.current.readyState === WebSocket.OPEN) {
                console.log('[teleop] sending disconnect command (disconnectControlWebSocket)');
                controlWsRef.current.send(JSON.stringify({command: 'disconnect'}));
                await new Promise(resolve => setTimeout(resolve, 100));
            }
            controlWsRef.current.close();
            controlWsRef.current = null;
        }
        controlTokenRef.current = null;
        stopKeyboardPolling();
        stopCountdown();
        stopInactivityTimer();
        if (expirationTimeoutRef.current) {
            clearTimeout(expirationTimeoutRef.current);
            expirationTimeoutRef.current = null;
        }
    };

    const startKeyboardPolling = () => {
        pollIntervalRef.current = setInterval(() => {
            if (controlWsRef.current && controlWsRef.current.readyState === WebSocket.OPEN) {
                sendKeyboardData();
            }
        }, 1000 / POLL_RATE);
    };

    const stopKeyboardPolling = () => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    };

    const startCountdown = (expiresTimestamp) => {
        // Stop any existing countdown
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
        }

        // Update remaining time immediately
        const updateRemainingTime = () => {
            const now = Date.now();
            const remaining = Math.max(0, Math.floor((expiresTimestamp * 1000 - now) / 1000));
            setRemainingTime(remaining);

            if (remaining <= 0) {
                clearInterval(countdownIntervalRef.current);
                countdownIntervalRef.current = null;
            }
        };

        updateRemainingTime();
        countdownIntervalRef.current = setInterval(updateRemainingTime, 1000);
    };

    const stopCountdown = () => {
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        setRemainingTime(0);
    };


    const sendKeyboardData = () => {
        const state = {};
        let pressed = false;

        // Speed scalar `spe` (default 0.5; hold Shift for 0.4)
        let speed = 0.8;
        if (keysPressed.current.has('ShiftLeft') || keysPressed.current.has('ShiftRight')) {
            speed = 0.4;
        }
        state.spe = speed;

        ['x', 'y', 'z', 't', 'r', 'g'].forEach(axis => {
            let v = 0;
            if (keysPressed.current.has(KEYMAP_PLUS[axis])) {
                v += 1; // normalized forward
                pressed = true;
            }
            if (keysPressed.current.has(KEYMAP_MINUS[axis])) {
                v -= 1; // normalized backward
                pressed = true;
            }
            state[axis] = v;
        });

        if (controlWsRef.current && controlWsRef.current.readyState === WebSocket.OPEN) {
            controlWsRef.current.send(JSON.stringify(state));
        }
    };

    // Keyboard event handlers
    useEffect(() => {
        // Allow chat input to receive keys without triggering teleop controls.
        const isEventFromChatInput = (event) => {
            const target = event.target;
            const matchesChatInput = (el) => {
                return el instanceof Element && Boolean(el.closest('[data-teleop-chat-input="true"]'));
            };
            if (matchesChatInput(target)) {
                return true;
            }
            const activeEl = document.activeElement;
            return matchesChatInput(activeEl);
        };

        const handleKeyDown = (event) => {
            if (!isTeleOpMode || isEventFromChatInput(event)) {
                return;
            }

            const controlKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'KeyW', 'KeyA', 'KeyS', 'KeyD', 'KeyQ', 'KeyE', 'KeyX', 'KeyZ', 'KeyC', 'KeyV'];
            if (controlKeys.includes(event.code)) {
                event.preventDefault();
                event.stopPropagation();
                // Track user activity when control keys are pressed
                trackUserActivity();
                startInactivityTimer();
            }

            if (event.code === 'KeyC' && !sliderKeysPressed.current.has('KeyC')) {
                sliderKeysPressed.current.add('KeyC');
                setPressedKeysUI(prev => new Set([...prev, event.code]));
                if (controlWsRef.current && controlWsRef.current.readyState === WebSocket.OPEN) {
                    controlWsRef.current.send(JSON.stringify({'c': true}));
                }
                return;
            }

            if (event.code === 'KeyV' && !sliderKeysPressed.current.has('KeyV')) {
                sliderKeysPressed.current.add('KeyV');
                setPressedKeysUI(prev => new Set([...prev, event.code]));
                if (controlWsRef.current && controlWsRef.current.readyState === WebSocket.OPEN) {
                    controlWsRef.current.send(JSON.stringify({'v': true}));
                }
                return;
            }

            if (!keysPressed.current.has(event.code)) {
                keysPressed.current.add(event.code);
                setPressedKeysUI(prev => new Set([...prev, event.code]));
            }
        };

        const handleKeyUp = (event) => {
            if (!isTeleOpMode || isEventFromChatInput(event)) {
                return;
            }
            const controlKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'KeyW', 'KeyA', 'KeyS', 'KeyD', 'KeyQ', 'KeyE', 'KeyX', 'KeyZ', 'KeyC', 'KeyV'];
            if (controlKeys.includes(event.code)) {
                event.preventDefault();
                event.stopPropagation();
            }

            if (event.code === 'KeyC' || event.code === 'KeyV') {
                sliderKeysPressed.current.delete(event.code);
                if (controlWsRef.current && controlWsRef.current.readyState === WebSocket.OPEN) {
                    controlWsRef.current.send(JSON.stringify({'slider': 'stop'}));
                }
            } else {
                keysPressed.current.delete(event.code);
            }

            setPressedKeysUI(prev => {
                const newSet = new Set(prev);
                newSet.delete(event.code);
                return newSet;
            });
        };

        // Prevent scrolling with arrow keys when in teleop mode
        const preventScroll = (event) => {
            if (!isTeleOpMode || isEventFromChatInput(event)) {
                return;
            }
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.code)) {
                event.preventDefault();
                return false;
            }
        };

        // Add event listeners
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        window.addEventListener('keydown', preventScroll, {passive: false});

        // Cleanup
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('keydown', preventScroll);
            keysPressed.current.clear();
            sliderKeysPressed.current.clear();
            setPressedKeysUI(new Set());
        };
    }, [isTeleOpMode]);

    const handleVirtualKeyPress = (keyCode) => {
        if (isTeleOpMode) {
            trackUserActivity();
            startInactivityTimer();

            if (keyCode === 'KeyC' && !sliderKeysPressed.current.has('KeyC')) {
                sliderKeysPressed.current.add('KeyC');
                setPressedKeysUI(prev => new Set([...prev, keyCode]));
                if (controlWsRef.current && controlWsRef.current.readyState === WebSocket.OPEN) {
                    controlWsRef.current.send(JSON.stringify({'c': true}));
                }
                return;
            }

            if (keyCode === 'KeyV' && !sliderKeysPressed.current.has('KeyV')) {
                sliderKeysPressed.current.add('KeyV');
                setPressedKeysUI(prev => new Set([...prev, keyCode]));
                if (controlWsRef.current && controlWsRef.current.readyState === WebSocket.OPEN) {
                    controlWsRef.current.send(JSON.stringify({'v': true}));
                }
                return;
            }

            if (!keysPressed.current.has(keyCode)) {
                keysPressed.current.add(keyCode);
                setPressedKeysUI(prev => new Set([...prev, keyCode]));
            }
        }
    };

    const handleVirtualKeyRelease = (keyCode) => {
        if (keyCode === 'KeyC' || keyCode === 'KeyV') {
            sliderKeysPressed.current.delete(keyCode);
            if (controlWsRef.current && controlWsRef.current.readyState === WebSocket.OPEN) {
                controlWsRef.current.send(JSON.stringify({'slider': 'stop'}));
            }
        } else {
            keysPressed.current.delete(keyCode);
        }

        setPressedKeysUI(prev => {
            const newSet = new Set(prev);
            newSet.delete(keyCode);
            return newSet;
        });
    };

    useEffect(() => {
        if (isTeleOpMode) {
            setIsControlPanelOpen(true);
        } else {
            setIsQueuePanelOpen(prev => prev === undefined || prev === null ? true : prev);
        }
    }, [isTeleOpMode]);

    // Video WebSocket connection - only connect when user has control
    useEffect(() => {
        const connectWebSocket = () => {
            try {
                if (!stream1UrlRef.current && !stream2UrlRef.current) {
                    return;
                }

                const videoUrl1 = stream1UrlRef.current || '';
                const videoUrl2 = stream2UrlRef.current || '';
                wsRef.current = new WebSocket(videoUrl1);
                ws2Ref.current = new WebSocket(videoUrl2);

                wsRef.current.onopen = () => {
                    setIsConnected(true);
                    wsRef.current.send(JSON.stringify({type: 'ping'}));
                };

                wsRef.current.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);

                        if (data.type === 'video_frame') {
                            setVideoSrc(`data:image/jpeg;base64,${data.data}`);

                            frameCountRef.current++;
                            const now = Date.now();
                            const elapsed = now - lastFpsUpdateRef.current;
                            if (elapsed >= 1000) {
                                setFps(Math.round((frameCountRef.current * 1000) / elapsed));
                                frameCountRef.current = 0;
                                lastFpsUpdateRef.current = now;
                            }
                        } else if (data.type === 'pong') {
                            console.log('Received pong from WebSocket 1');
                        }
                    } catch (error) {
                        console.error('Error parsing message from WebSocket 1:', error);
                    }
                };

                wsRef.current.onerror = (error) => {
                    console.error('WebSocket 1 error:', error);
                    setIsConnected(false);
                };

                wsRef.current.onclose = () => {
                    setIsConnected(false);
                    setVideoSrc(null);
                    // Only reconnect if it's not an intentional disconnect and still in teleop mode
                    if (isTeleOpMode && !isIntentionalDisconnectRef.current) {
                        setTimeout(connectWebSocket, 3000);
                    } else if (isIntentionalDisconnectRef.current) {
                    }
                };

                // Handle second WebSocket
                ws2Ref.current.onopen = () => {
                    ws2Ref.current.send(JSON.stringify({type: 'ping'}));
                };

                ws2Ref.current.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);

                        if (data.type === 'video_frame') {
                            setVideoSrc2(`data:image/jpeg;base64,${data.data}`);
                        } else if (data.type === 'pong') {
                            console.log('Received pong from WebSocket 2');
                        }
                    } catch (error) {
                        console.error('Error parsing message from WebSocket 2:', error);
                    }
                };

                ws2Ref.current.onerror = (error) => {
                    console.error('WebSocket 2 error:', error);
                };

                ws2Ref.current.onclose = () => {
                    console.log('Disconnected from WebSocket 2');
                    setVideoSrc2(null);
                };
            } catch (error) {
                console.error('Failed to connect:', error);
                setIsConnected(false);
                // Only retry if still in teleop mode and not an intentional disconnect
                if (isTeleOpMode && !isIntentionalDisconnectRef.current) {
                    setTimeout(connectWebSocket, 3000);
                }
            }
        };

        if (isTeleOpMode) {
            // Reset the intentional disconnect flag when entering teleop mode
            isIntentionalDisconnectRef.current = false;
            connectWebSocket();
        } else {
            // Disconnect when not in teleop mode
            if (wsRef.current) {
                // Mark as intentional disconnect when leaving teleop mode
                isIntentionalDisconnectRef.current = true;
                wsRef.current.close();
                wsRef.current = null;
                setIsConnected(false);
                setVideoSrc(null);
            }
            if (ws2Ref.current) {
                ws2Ref.current.close();
                ws2Ref.current = null;
                setVideoSrc2(null);
            }
        }

        return () => {
            if (wsRef.current) {
                // Mark as intentional disconnect when component unmounts
                isIntentionalDisconnectRef.current = true;
                wsRef.current.close();
            }
            if (ws2Ref.current) {
                ws2Ref.current.close();
            }
        };
    }, [isTeleOpMode, stream1UrlRef.current, stream2UrlRef.current]);

    useEffect(() => {
        const pingInterval = setInterval(() => {
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({type: 'ping'}));
            }
            if (ws2Ref.current && ws2Ref.current.readyState === WebSocket.OPEN) {
                ws2Ref.current.send(JSON.stringify({type: 'ping'}));
            }
        }, 30000);

        return () => clearInterval(pingInterval);
    }, []);


    const handleEndTeleOp = async (reasonOrOptions = 'manual') => {
        // Determine why the session ended so we can report and show the right UI.
        const reason = typeof reasonOrOptions === 'string'
            ? reasonOrOptions
            : reasonOrOptions?.reason || 'manual';
        const isInactive = reason === 'inactivity';
        let didExpireUserSession = false;

        if (controlWsRef.current) {
            console.log('Closing control WebSocket connection...');
            if (controlWsRef.current.readyState === WebSocket.OPEN) {
                console.log('[teleop] sending disconnect command (handleEndTeleOp)');
                controlWsRef.current.send(JSON.stringify({command: 'disconnect'}));
                await new Promise(resolve => setTimeout(resolve, 100));
            }
            controlWsRef.current.close();
            controlWsRef.current = null;
        }

        if (wsRef.current) {
            // Mark as intentional disconnect when session closes from the client.
            isIntentionalDisconnectRef.current = true;
            wsRef.current.close();
            wsRef.current = null;
            setIsConnected(false);
            setVideoSrc(null);
        }

        if (ws2Ref.current) {
            ws2Ref.current.close();
            ws2Ref.current = null;
            setVideoSrc2(null);
        }

        stopKeyboardPolling();
        stopCountdown();
        stopInactivityTimer();
        if (expirationTimeoutRef.current) {
            clearTimeout(expirationTimeoutRef.current);
            expirationTimeoutRef.current = null;
        }

        const userId = localStorage.getItem('userId');
        const robotId = controlData?.robotId || effectiveRobotId || 'arm1';

        if (userId && gatewayToken) {
            try {
                const bodyData = {
                    userId: userId,
                    robotId: robotId
                };

                if (isInactive) {
                    bodyData.inactive = true;
                }

                const response = await fetch(`${PRISMAX_BACKEND_Control_URL}/queue/leave`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${gatewayToken}`
                    },
                    body: JSON.stringify(bodyData)
                });

                if (!response.ok) {
                    console.error('Failed to notify backend of session end');
                    const error = await response.json().catch(() => ({}));
                    console.error('Leave queue error:', error);
                    const message = error.error || error.msg || '';
                    if (isSessionExpiredError(message)) {
                        didExpireUserSession = true;
                        handleUserSessionExpired?.();
                    }
                } else {
                    // console.log('Successfully notified backend of session end');
                }
            } catch (error) {
                console.error('Error ending session:', error);
            }
        }

        // Reset all local state
        const tokenForModal = controlTokenRef.current; // capture before clearing
        setIsTeleOpMode(false);
        setIsControlConnected(false);
        setControlData(null);
        hasConnectedRef.current = false;
        isConnectingRef.current = false;
        controlTokenRef.current = null;
        keysPressed.current.clear();
        sliderKeysPressed.current.clear();
        setPressedKeysUI(new Set());
        stream1UrlRef.current = null;
        stream2UrlRef.current = null;

        if (didExpireUserSession) {
            return;
        }

        if (isFirstTime) {
            setShowFirstTimeModal(true);
        } else {
            setModalControlToken(tokenForModal || null);
            if (robotDataDict[robotId]?.robotSlug === 'arena-arm') {
                setShowSessionCompleteModal(true);
            }
        }
    };

    return (
        <div className={`${styles.container} ${styles.globalContainer}`}>
            <div
                className={`${styles.mainContent} ${(!isQueuePanelOpen && !isTeleOpMode) || (!isControlPanelOpen && isTeleOpMode) ? styles.mainContentExpanded : ''}`}>
                <div className={styles.navBar}>
                    <Link to="/live-control" className={styles.navLink}>
                        Live&nbsp;Control
                    </Link>

                    <span style={{marginInline: '10px'}}>&nbsp;|&nbsp;</span>
                    <Link to="/robots-center" className={styles.navLink}>
                        Robots Center
                    </Link>
                    <img style={{marginInline: '10px'}} src={navRightArrow} alt="arrow"/>

                    <span className={styles.activeNavBar}>
                        {robotName.replace(' ', '\u00a0')}
                    </span>
                </div>

                <VideoFeed
                    robotId={effectiveRobotId}
                    isConnected={isConnected}
                    isControlConnected={isControlConnected}
                    isTeleOpMode={isTeleOpMode}
                    videoSrc={videoSrc}
                    videoSrc2={videoSrc2}
                />


                {!isTeleOpMode ? (
                    <KeyboardControls
                        keysPressed={pressedKeysUI}
                        isControlConnected={isControlConnected}
                        onVirtualKeyPress={handleVirtualKeyPress}
                        onVirtualKeyRelease={handleVirtualKeyRelease}
                        robotId={effectiveRobotId}
                    />
                ) : (
                    <KeyboardControls
                        keysPressed={pressedKeysUI}
                        isControlConnected={isControlConnected}
                        onVirtualKeyPress={handleVirtualKeyPress}
                        onVirtualKeyRelease={handleVirtualKeyRelease}
                        robotId={effectiveRobotId}
                    />
                )}
            </div>

            <TeleOpRightPanel
                robotId={effectiveRobotId}
                leftPanel={isTeleOpMode ? 'controls' : 'queue'}
                // QueuePanel props
                selectedRobot={effectiveRobotId}
                setIsTeleOpMode={setIsTeleOpMode}
                gatewayToken={gatewayToken}
                userId={userId}
                PRISMAX_BACKEND_Control_URL={PRISMAX_BACKEND_Control_URL}
                onControlDataReceived={handleControlDataReceived}
                isQueuePanelOpen={isQueuePanelOpen}
                setIsQueuePanelOpen={setIsQueuePanelOpen}
                sessionExpired={sessionExpired}
                setSessionExpired={setSessionExpired}
                // ControlsPanel props
                isControlConnected={isControlConnected}
                onEndTeleOp={handleEndTeleOp}
                isControlPanelOpen={isControlPanelOpen}
                setIsControlPanelOpen={setIsControlPanelOpen}
                remainingTime={remainingTime}
            />

            {!isChatOpen && !isTeleOpMode && (
                <button
                    className={styles.openChatBtn}
                    onClick={() => setIsChatOpen(true)}
                >
                    Open Chat
                </button>
            )}

            <FirstTimeCompleteModal
                isOpen={showFirstTimeModal}
                onClose={() => setShowFirstTimeModal(false)}
            />
            {showSessionCompleteModal && !showFirstTimeModal && (
                <TeleopSessionCompleteModal
                    controlToken={modalControlToken}
                    onClose={() => {
                        setShowSessionCompleteModal(false);
                        setModalControlToken(null);
                    }}
                />
            )}
        </div>
    );
};

export default TeleOp;
